import i18n from 'i18next';
import type { ErroApi } from '@malgoos/shared';
import { CODIGOS_PT_CURADOS } from '../i18n/erros-pt';
import { censurarTexto, nomesEscondidos, numero } from './formato';
import { rotuloModulo } from './rotulos';

/** AAAA-MM (competência) dentro de um texto vira MM/AAAA, sem tocar em datas completas. */
function competenciasLegiveis(texto: string) {
  return texto.replace(/(?<![\d-])(\d{4})-(\d{2})(?![\d-])/g, '$2/$1');
}

type Dicionario = Record<string, string>;
const curingas = new WeakMap<Dicionario, [string, string][]>();

/** Dicionário de erros por código do idioma atual (chave plana: o código tem pontos, então não passa pelo `t`). */
function dicionarioDeErros(idioma: string): Dicionario | undefined {
  const pacote = i18n.getResourceBundle(idioma, 'nucleo') as { erros?: { codigos?: Dicionario } } | undefined;
  return pacote?.erros?.codigos;
}

/** Texto do código: chave exata ou curinga ('*.invalida' vale para `${campo}.invalida`; 'funcionario.ja_*' para o prefixo). */
function textoDoCodigo(dic: Dicionario, codigo: string): string | undefined {
  if (Object.hasOwn(dic, codigo)) return dic[codigo];
  let lista = curingas.get(dic);
  if (!lista) {
    lista = Object.entries(dic).filter(([k]) => k.includes('*'));
    curingas.set(dic, lista);
  }
  const resto = codigo.slice(codigo.indexOf('.') + 1);
  for (const [k, v] of lista) {
    if (k.startsWith('*.') ? resto === k.slice(2) && codigo.includes('.') : codigo.startsWith(k.slice(0, -1))) return v;
  }
  return undefined;
}

const idiomaAtual = () => (i18n.resolvedLanguage ?? i18n.language ?? 'pt').slice(0, 2);

/**
 * Texto do código no idioma atual, com os valores de `meta` (e a competência achada na mensagem) interpolados; `undefined` se o
 * código não tem tradução ou falta valor. Em pt só os códigos curados (ou sem mensagem do servidor): lá a mensagem do servidor
 * já está no idioma e é mais rica.
 */
/**
 * r8 (categoria.em_uso em en/es): o mesmo código chega em contextos diferentes. Com `meta.usos` (conferência de uso do
 * kernel, `falhaEmUso`) a frase lista os usos por módulo no idioma; com `meta.campo` vale a variante `codigo#campo` do
 * dicionário, quando existe (troca de tipo da categoria); senão, o texto do código.
 */
function varianteDoErro(dic: Dicionario, codigo: string, meta?: Record<string, unknown>): string | undefined {
  const usos = meta?.usos;
  if (codigo.endsWith('.em_uso') && Array.isArray(usos) && usos.length) {
    const lista = (usos as { modulo?: string; quantidade?: number; chave?: string }[])
      .filter((u) => u && typeof u.modulo === 'string')
      .map((u) => {
        // Fase 2 (W8): uso com `chave` tem frase própria no módulo dono (`<slug>:modos.usos.<chave>`), com a quantidade.
        const frase = u.chave && typeof u.quantidade === 'number' ? `${u.modulo}:modos.usos.${u.chave}` : null;
        if (frase && i18n.exists(`${frase}_other`)) return i18n.t(frase, { count: u.quantidade, n: numero(u.quantidade!) });
        return typeof u.quantidade === 'number' ? `${rotuloModulo(u.modulo!)} (${numero(u.quantidade)})` : rotuloModulo(u.modulo!);
      })
      .join('; ');
    if (lista) return i18n.t('erros.emUso', { lista });
  }
  const campo = typeof meta?.campo === 'string' ? meta.campo : undefined;
  return campo && Object.hasOwn(dic, `${codigo}#${campo}`) ? dic[`${codigo}#${campo}`] : undefined;
}

/** Nome da página (`<módulo>.<aba>`) no idioma, com o módulo entre parênteses; sem tradução, a chave humanizada (nunca o código cru). */
function rotuloPagina(chave: string): string {
  const i = chave.indexOf('.');
  const slug = chave.slice(0, i);
  const aba = chave.slice(i + 1);
  if (i > 0 && i18n.exists(`${slug}:abas.${aba}`)) return `${i18n.t(`${slug}:abas.${aba}`)} (${i18n.exists(`${slug}:nome`) ? i18n.t(`${slug}:nome`) : rotuloModulo(slug)})`;
  return aba.replace(/[._-]+/g, ' ');
}

/** `meta.paginas` (lista de chaves de página) quando o servidor mandou; recusa por página tem frase própria (`codigo#paginas`). */
function paginasDoMeta(meta?: Record<string, unknown>): string[] {
  const p = meta?.paginas;
  return Array.isArray(p) ? p.filter((x): x is string => typeof x === 'string' && x.length > 0) : [];
}

function textoTraduzidoDoCodigo(codigo: string | undefined, mensagem: string, meta?: Record<string, unknown>): string | undefined {
  if (!codigo) return undefined;
  const idioma = idiomaAtual();
  const dic = dicionarioDeErros(idioma);
  // Com os nomes escondidos na tela (r10 visual B4), a mensagem do servidor pode citar um nome ("Documento já cadastrado em
  // Ana Souza"); em pt vale o texto do dicionário, que nunca traz nome. O estado fica só no cliente (nada vai ao servidor).
  const paginas = paginasDoMeta(meta);
  // Rodada rval1-9 (SPEC-B1): a recusa por página em OUTRO workspace (edição de papel do tenant, `meta.workspace_id`) tem frase
  // própria; a do workspace atual segue a geral.
  const outroWs = typeof meta?.workspace_id === 'string' && !!dic && Object.hasOwn(dic, `${codigo}#paginas_outro_workspace`);
  const chavePaginas = outroWs ? `${codigo}#paginas_outro_workspace` : `${codigo}#paginas`;
  const porPagina = !!dic && paginas.length > 0 && Object.hasOwn(dic, chavePaginas) ? dic[chavePaginas] : undefined;
  const usar = !!dic && (idioma !== 'pt' || CODIGOS_PT_CURADOS.includes(codigo) || !mensagem || nomesEscondidos() || !!porPagina);
  const modelo = usar ? (porPagina ?? varianteDoErro(dic!, codigo, meta) ?? textoDoCodigo(dic!, codigo)) : undefined;
  if (!modelo) return undefined;
  const comp = /(?<![\d-])(\d{4})-(\d{2})(?![\d-])/.exec(mensagem);
  const valores: Record<string, unknown> = { ...meta, competencia: comp ? `${comp[2]}/${comp[1]}` : '' };
  if (porPagina) valores.paginas = paginas.map(rotuloPagina).join(', ');
  const texto = modelo.replace(/\{\{(\w+)\}\}/g, (_, k: string) => (valores[k] === undefined || valores[k] === null ? '{{' : String(valores[k])));
  return texto.includes('{{') ? undefined : texto; // valor que o servidor não mandou: cai no texto do servidor
}

/**
 * Mensagem de erro de campo ou geral no idioma atual: código de domínio com tradução própria (`erros.codigos`,
 * com `meta` do servidor para os valores), texto técnico do validador (Zod) em frase amigável, ou o texto do
 * servidor com competência legível. Em pt a mensagem do servidor já está no idioma; só os códigos curados a trocam.
 */
export function traduzirErro(codigo: string | undefined, mensagem: string, meta?: Record<string, unknown>): string {
  const t = i18n.t.bind(i18n);
  const traduzido = textoTraduzidoDoCodigo(codigo, mensagem, meta);
  if (traduzido) return traduzido;
  if (/^Op[cç][aã]o inv[aá]lida/i.test(mensagem)) return t('erros.opcao');
  if (/recebeu um valor "?(undefined|null)"?/i.test(mensagem) || /^Entrada inv[aá]lida: esperava/i.test(mensagem)) return t('erros.obrigatorio');
  if (/^Entrada inv[aá]lida$/i.test(mensagem) || codigo === 'entrada.invalida') return t('erros.entradaInvalida');
  if (/^(Muito|Too|N[uú]mero|String|Number|Invalid)\b/i.test(mensagem)) return t('erros.valor');
  // r10 visual B4: com os valores escondidos na tela, valor, documento, e-mail, telefone e CEP citados pelo servidor somem.
  return censurarTexto(competenciasLegiveis(mensagem));
}

const CODIGO = /^[a-z][a-z0-9_]*(\.[a-z0-9_]+)+$/;

/**
 * Erro gravado no servidor (fila, conector, job) que é só um código de domínio ("importacao.sem_nome") ou "codigo: mensagem"
 * (recusa de job do worker, `recusaDe`: "competencia.centro_custo_invalido: Há centro..."; r6 robustez B3): traduz pela mesma
 * tabela de `traduzirErro`, inclusive em pt, e nunca mostra o código cru. Outro texto passa como veio.
 */
export function textoDeErroGravado(texto: string | null | undefined): string {
  if (!texto) return '';
  const limpo = texto.trim();
  const par = /^([a-z][a-z0-9_]*(?:\.[a-z0-9_]+)+):\s*([\s\S]+)$/.exec(limpo);
  if (par) return traduzirErro(par[1], par[2]!.trim());
  if (!CODIGO.test(limpo)) return texto;
  const dic = dicionarioDeErros(idiomaAtual()) ?? dicionarioDeErros('pt');
  const modelo = dic ? textoDoCodigo(dic, limpo) : undefined;
  return modelo && !modelo.includes('{{') ? modelo : texto;
}

export class ErroRequisicao extends Error {
  constructor(
    public readonly status: number,
    public readonly corpo: ErroApi,
    /** Espera pedida pelo servidor (`Retry-After` ou `details.segundos`) num 409 ou 429; null sem pedido. */
    public readonly esperaSegundos: number | null = null,
  ) {
    super(corpo.message);
  }

  /** Meta do erro principal: do item de `details` com o mesmo código (Result do domínio) ou do objeto de detalhes (ErroHttp). */
  metaDoErro(): Record<string, unknown> | undefined {
    const d = this.corpo.details;
    if (Array.isArray(d)) {
      const item = (d as { codigo?: string; meta?: Record<string, unknown> }[]).find((i) => i && i.codigo === this.corpo.code) ?? (d[0] as { meta?: Record<string, unknown> } | undefined);
      return item?.meta;
    }
    return d && typeof d === 'object' ? (d as Record<string, unknown>) : undefined;
  }

  /** Erros por campo vindos de `details` (Zod ou Result do domínio). */
  porCampo(): Record<string, string> {
    const saida: Record<string, string> = {};
    const d = this.corpo.details;
    if (Array.isArray(d)) {
      for (const item of d as { campo?: string; codigo?: string; mensagem?: string; meta?: Record<string, unknown> & { campo?: string } }[]) {
        const campo = item.campo ?? item.meta?.campo;
        if (campo && item.mensagem && !saida[campo]) saida[campo] = traduzirErro(item.codigo, item.mensagem, item.meta);
      }
    }
    return saida;
  }
}

type Metodo = 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE';

let aoExpirar: (() => void) | null = null;
export function definirAoExpirar(fn: () => void) {
  aoExpirar = fn;
}

function montarUrl(caminho: string, params?: Record<string, unknown>) {
  const url = new URL(caminho.startsWith('/api') || caminho.startsWith('/health') ? caminho : `/api/v1${caminho}`, window.location.origin);
  for (const [k, v] of Object.entries(params ?? {})) {
    if (v === undefined || v === null || v === '') continue;
    url.searchParams.set(k, Array.isArray(v) ? v.join(',') : String(v));
  }
  return url.toString();
}

/**
 * Conflito passageiro com espera dada pelo servidor (r10 robustez, `recorrencia.conflito`: a emissão automática segura a
 * recorrência por instantes). Nada foi gravado, então o cliente espera o `Retry-After` (até 10 s) e tenta UMA vez de novo; se
 * ainda houver conflito, a mensagem traduzida chega à tela.
 */
const CONFLITOS_COM_ESPERA = new Set(['recorrencia.conflito']);
const ESPERA_MAXIMA_S = 10;

/** Segundos de espera de uma resposta 409/429: cabeçalho `Retry-After` ou `details.segundos`. */
export function segundosDeEspera(cabecalho: string | null, corpo: unknown): number | null {
  const doCabecalho = cabecalho !== null && /^\d+$/.test(cabecalho.trim()) ? Number(cabecalho) : null;
  const d = (corpo as { details?: unknown } | undefined)?.details;
  const doCorpo = d && typeof d === 'object' && !Array.isArray(d) && typeof (d as { segundos?: unknown }).segundos === 'number' ? (d as { segundos: number }).segundos : null;
  const s = doCabecalho ?? doCorpo;
  return s === null || !Number.isFinite(s) || s < 0 ? null : s;
}

export async function api<T = unknown>(
  metodo: Metodo,
  caminho: string,
  corpo?: unknown,
  opcoes: { params?: Record<string, unknown>; headers?: Record<string, string>; semRepetir?: boolean; signal?: AbortSignal } = {},
): Promise<T> {
  try {
    return await chamar<T>(metodo, caminho, corpo, opcoes);
  } catch (e) {
    const espera = e instanceof ErroRequisicao && e.status === 409 && CONFLITOS_COM_ESPERA.has(e.corpo.code) ? e.esperaSegundos : null;
    if (opcoes.semRepetir || espera === null || espera > ESPERA_MAXIMA_S) throw e;
    await new Promise((r) => setTimeout(r, Math.max(espera, 1) * 1000));
    return chamar<T>(metodo, caminho, corpo, opcoes);
  }
}

/**
 * V2 verticais: filtro de empresa desta aba ("Minhas verticais" ou uma empresa), guardado no sessionStorage (cada aba tem o
 * seu) e mandado em toda requisição no cabeçalho `X-Empresa` (`todas` = sem filtro; o servidor recalcula o escopo).
 */
const CHAVE_FILTRO_EMPRESA = 'malgoos.filtro_empresa';
export const FILTRO_SEM_EMPRESA = 'todas';
let filtroEmpresa: string | null = (() => {
  try {
    return sessionStorage.getItem(CHAVE_FILTRO_EMPRESA);
  } catch {
    return null;
  }
})();
export const filtroEmpresaDaAba = () => filtroEmpresa;
export function definirFiltroEmpresa(empresaId: string | null) {
  filtroEmpresa = empresaId;
  try {
    if (empresaId) sessionStorage.setItem(CHAVE_FILTRO_EMPRESA, empresaId);
    else sessionStorage.removeItem(CHAVE_FILTRO_EMPRESA);
  } catch {
    // sem armazenamento (aba privada): o filtro vale até recarregar
  }
}
/**
 * val4 (29/10): a simulação de usuário de teste que a tela mostra (id, da sessão). Vai em toda requisição no cabeçalho
 * `X-Simulacao`; se ela já acabou no servidor (vencida, encerrada noutra aba), a API responde 409 `simulacao.encerrada` sem
 * executar nada e a página recarrega como a conta de quem simulava.
 */
let simulacaoDaTela: string | null = null;
export const definirSimulacaoDaTela = (id: string | null) => {
  simulacaoDaTela = id;
};
export const simulacaoAtualDaTela = () => simulacaoDaTela;
let aoSimulacaoEncerrada: () => void = () => window.location.assign('/');
export const definirAoSimulacaoEncerrada = (fn: () => void) => {
  aoSimulacaoEncerrada = fn;
};

/** Cabeçalho que leva a empresa escolhida no formulário (registro separado por empresa), no lugar do filtro da aba. */
export const cabecalhoEmpresa = (empresaId: string | null | undefined): Record<string, string> => (empresaId ? { 'X-Empresa': empresaId } : {});

async function chamar<T>(metodo: Metodo, caminho: string, corpo: unknown, opcoes: { params?: Record<string, unknown>; headers?: Record<string, string>; signal?: AbortSignal }): Promise<T> {
  const ehForm = corpo instanceof FormData;
  const r = await fetch(montarUrl(caminho, opcoes.params), {
    method: metodo,
    credentials: 'same-origin',
    headers: {
      Accept: 'application/json',
      ...(corpo !== undefined && !ehForm ? { 'Content-Type': 'application/json' } : {}),
      'X-Empresa': filtroEmpresa ?? FILTRO_SEM_EMPRESA,
      ...(simulacaoDaTela ? { 'X-Simulacao': simulacaoDaTela } : {}),
      ...opcoes.headers,
    },
    body: corpo === undefined ? undefined : ehForm ? corpo : JSON.stringify(corpo),
    // Lista de 30/10 (item 10): a busca global cancela a requisição do termo anterior.
    signal: opcoes.signal,
  });
  if (r.status === 204) return undefined as T;
  const texto = await r.text();
  let json: unknown;
  try {
    json = texto ? JSON.parse(texto) : undefined;
  } catch {
    json = undefined; // proxy ou servidor fora devolvendo HTML ou texto
  }
  if (!r.ok) {
    if (r.status === 401 && !caminho.startsWith('/auth/login')) aoExpirar?.();
    if (r.status === 409 && (json as ErroApi | undefined)?.code === 'simulacao.encerrada') {
      simulacaoDaTela = null;
      aoSimulacaoEncerrada();
    }
    throw new ErroRequisicao(r.status, (json as ErroApi | undefined) ?? { code: r.status >= 500 ? 'servidor.indisponivel' : 'erro', message: '' }, segundosDeEspera(r.headers.get('Retry-After'), json));
  }
  return json as T;
}

export const get = <T>(caminho: string, params?: Record<string, unknown>, opcoes?: { signal?: AbortSignal }) => api<T>('GET', caminho, undefined, { params, signal: opcoes?.signal });
export const post = <T>(caminho: string, corpo?: unknown, headers?: Record<string, string>) => api<T>('POST', caminho, corpo ?? {}, { headers });
export const patch = <T>(caminho: string, corpo?: unknown) => api<T>('PATCH', caminho, corpo ?? {});
export const del = <T>(caminho: string, params?: Record<string, unknown>) => api<T>('DELETE', caminho, undefined, { params });

/**
 * Lista paginada inteira (selects e listas curtas de apoio): segue `proximo_cursor` em páginas de 200,
 * até `maxPaginas`. O resto da primeira resposta (ex.: `centros`) é preservado.
 */
export async function getTodos<T, R extends { itens: T[]; proximo_cursor?: string | null } = { itens: T[]; proximo_cursor?: string | null }>(
  caminho: string,
  params: Record<string, unknown> = {},
  maxPaginas = 25,
): Promise<R> {
  const primeira = await get<R>(caminho, { ...params, limite: 200 });
  const itens = [...primeira.itens];
  let cursor = primeira.proximo_cursor;
  for (let i = 1; cursor && i < maxPaginas; i++) {
    const p = await get<R>(caminho, { ...params, limite: 200, cursor });
    itens.push(...p.itens);
    cursor = p.proximo_cursor;
  }
  return { ...primeira, itens, proximo_cursor: cursor ?? null };
}

/** Mensagem legível no idioma atual; falha de rede e servidor fora viram texto traduzido. */
export function mensagemDeErro(e: unknown, padrao?: string): string {
  const t = i18n.t.bind(i18n);
  if (e instanceof ErroRequisicao) {
    // r7 visual B2: o código traduzido vem antes da `message` do servidor (em pt), também quando a mensagem vem vazia.
    const { code, message } = e.corpo;
    const porCodigo = textoTraduzidoDoCodigo(code, message ?? '', e.metaDoErro());
    if (porCodigo) return porCodigo;
    // Falha do servidor sem tradução do código: fora do pt, o texto em pt do servidor não aparece; vale o texto genérico.
    if (e.status >= 500 && (!message || idiomaAtual() !== 'pt')) return t('estados.servidorIndisponivel');
    if (message) return traduzirErro(code, message, e.metaDoErro());
    return padrao ?? t('estados.erroTexto');
  }
  if (e instanceof TypeError) return t('estados.semConexao');
  // Fase 2 (W16): consulta segurada pelo recuo depois de um 429 ou 503 (`nucleo/recuo.ts`): o servidor pediu um tempo.
  if (e instanceof Error && e.name === 'ErroEmRecuo') return t('estados.servidorIndisponivel');
  // Validação local: `throw new Error(t('...'))` já vem traduzida e deve chegar à tela (erros nativos como SyntaxError não).
  if (e instanceof Error && e.constructor === Error && e.message.trim()) return e.message;
  return padrao ?? t('estados.erroTexto');
}
